import { useMemo } from 'react';
import {
  type MeasureDataByEntity,
  type MeasureMetadata,
} from '../../../../../api/work-periods-client/work-periods-client.type';
import { useProcessAnalysisStore } from '../../../../../store/process-analysis-store/process-analysis-store';
import {
  useEntities,
  useSelectedMeasureOverTime,
  useSelectedMeasureOverTimeTrend,
  useTimeAllocation,
  useTimeAllocations,
} from '../../../../../store/process-analysis-store/process-analysis-store.hooks';
import { getTimeAllocationChartLabels } from '../../../components/line-chart/line-chart.helpers';
import type { ChartDataItem } from '../../../components/line-chart/line-chart.type';
import { calculateTrend } from '../../measure-comparison/line-chart/line-chart.helpers';

/**
 * Custom hook that transforms the measure data into chart data for Key Measures Over Time
 *
 * @param {MeasureDataByEntity | undefined} data - The measure data by entity to transform
 * @returns {ChartDataItem[]} - The chart data
 */
const useChartData = (data: MeasureDataByEntity | undefined): ChartDataItem[] => {
  const selectedMeasureOverTime = useSelectedMeasureOverTime();
  const selectedMeasureOverTimeTrend = useSelectedMeasureOverTimeTrend();
  const entities = useEntities();
  const timeAllocation = useTimeAllocation();
  const timeAllocations = useTimeAllocations();

  return useMemo(() => {
    if (!data || !selectedMeasureOverTime || !entities.length) {
      return [];
    }

    const allDates = new Set<string>();
    entities.forEach((entityId) => {
      const entityData = data[entityId];
      if (entityData && entityData[selectedMeasureOverTime]) {
        Object.keys(entityData[selectedMeasureOverTime]).forEach((date) => {
          allDates.add(date);
        });
      }
    });

    const sortedDates = Array.from(allDates).sort();

    const trendCalculations: Record<string, { slope: number; intercept: number }> = {};
    if (selectedMeasureOverTimeTrend) {
      entities.forEach((entityId) => {
        const entityData = data[entityId];
        if (entityData && entityData[selectedMeasureOverTime]) {
          const trendResult = calculateTrend(entityData[selectedMeasureOverTime], true);
          if (trendResult) {
            trendCalculations[entityId] = trendResult;
          }
        }
      });
    }

    return sortedDates.map((date, index) => {
      const { tickLabel, tooltipLabel } = getTimeAllocationChartLabels({
        rawDate: date,
        timeAllocationType: timeAllocation,
        timeAllocations,
      });

      const chartDataItem: ChartDataItem = {
        date: tickLabel,
        rawDate: date,
        dateRange: tooltipLabel,
      };

      entities.forEach((entityId) => {
        const entityData = data[entityId];
        const measureValues = entityData?.[selectedMeasureOverTime];
        const value = measureValues ? measureValues[date] : null;
        chartDataItem[entityId] = value;
      });

      if (selectedMeasureOverTimeTrend) {
        entities.forEach((entityId) => {
          const trendCalc = trendCalculations[entityId];
          if (trendCalc) {
            const trendValue = trendCalc.slope * index + trendCalc.intercept;
            chartDataItem[`${entityId}_trend`] = trendValue;
          }
        });
      }

      return chartDataItem;
    });
  }, [data, selectedMeasureOverTime, selectedMeasureOverTimeTrend, entities, timeAllocation, timeAllocations]);
};

/**
 * Custom hook that returns metadata for the currently selected measure over time
 *
 * @returns {MeasureMetadata | null} The metadata for the selected measure, or null if no measure is selected
 */
const useSelectedMeasureMetadata = (): MeasureMetadata | null => {
  const selectedMeasureOverTime = useSelectedMeasureOverTime();
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);

  const measureMetadata = useMemo(() => {
    if (!selectedMeasureOverTime) {
      return null;
    }

    return availableMeasures.find((m) => m.measure_name === selectedMeasureOverTime) || null;
  }, [selectedMeasureOverTime, availableMeasures]);

  return measureMetadata;
};

export { useChartData, useSelectedMeasureMetadata };
